import React, {useEffect, useState} from "react";
import { Container, Row, Col, Card, Form } from 'react-bootstrap';
import classes from "../../../styles/apps/word-counter.module.scss";
import elements from "../../../styles/settings/elements.module.scss";
import {Helmet} from "react-helmet";
import VerticalAd from "../../../components/adsense/VericalAd";
import {LEFT_SLOT, RIGHT_SLOT, SLOT1, SLOT2} from "../../../storage/constants/adsense";
import TrendingApps from "../../../components/TrendingApps";
import HorizontalAd from "../../../components/adsense/HorizontalAd";
import TrendingGames from "../../../components/TrendingGames";

interface Keyword {
    phrase: string;
    count: number;
    percentage: number;
}

const PAGE_SIZE = 5;

const WordCounter: React.FC = () => {
    const [text, setText] = useState("");
    const [keywordCategory, setKeywordCategory] = useState<1 | 2 | 3>(1);
    const [currentPage, setCurrentPage] = useState(1);

    // Basic counting functions
    const countSentences = (): number => {
        return text.split(/[.!?]+/).filter(sentence => sentence.trim() !== "").length;
    };

    const countWords = (): number => {
        if (text.trim() === "") return 0;
        return text.trim().split(/\s+/).length;
    };

    const countCharacters = (): number => text.length;

    // Basic heuristic for syllable counting per word
    const countSyllablesInWord = (word: string): number => {
        word = word.toLowerCase();
        if (word.length <= 3) return 1;
        word = word.replace(/e\b/, '');
        const matches = word.match(/[aeiouy]+/g);
        return matches ? matches.length : 1;
    };

    const countSyllables = (): number => {
        if (text.trim() === "") return 0;
        const tokens = text.trim().split(/\s+/);
        return tokens.reduce((sum, word) => sum + countSyllablesInWord(word), 0);
    };

    const countParagraphs = (): number => {
        if (text.trim() === "") return 0;
        return text.split(/\n+/).filter(p => p.trim() !== "").length;
    };

    // Convert a decimal minute value to MM:SS format
    const formatTime = (timeInMinutes: number): string => {
        const totalSeconds = Math.round(timeInMinutes * 60);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
    };

    const words = countWords();
    const characters = countCharacters();
    const sentences = countSentences();
    const syllables = countSyllables();
    const paragraphs = countParagraphs();
    const readingTime = formatTime(words / 200);  // Reading: 200 wpm
    const speakingTime = formatTime(words / 130); // Speaking: 130 wpm

    // ---------- Keyword Logic ----------
    const getTokens = (): string[] => {
        return text
            .toLowerCase()
            .replace(/[.,!?;:()"]/g, " ")
            .split(/\s+/)
            .filter(token => token !== "");
    };

    const computeOneWordKeywords = (): Keyword[] => {
        const tokens = getTokens();
        const total = tokens.length;
        const freq: Record<string, number> = {};
        tokens.forEach(token => {
            freq[token] = (freq[token] || 0) + 1;
        });
        const keywords: Keyword[] = [];
        Object.entries(freq).forEach(([word, count]) => {
            if (count > 1) {
                keywords.push({
                    phrase: word,
                    count,
                    percentage: total > 0 ? (count / total) * 100 : 0
                });
            }
        });
        return keywords.sort((a, b) => b.count - a.count);
    };

    const computeTwoWordKeywords = (): Keyword[] => {
        const tokens = getTokens();
        const total = tokens.length;
        const freq: Record<string, number> = {};
        for (let i = 0; i < tokens.length - 1; i++) {
            const phrase = `${tokens[i]} ${tokens[i + 1]}`;
            freq[phrase] = (freq[phrase] || 0) + 1;
        }
        const keywords: Keyword[] = [];
        Object.entries(freq).forEach(([phrase, count]) => {
            if (count > 1) {
                keywords.push({
                    phrase,
                    count,
                    percentage: total > 0 ? (count / total) * 100 : 0
                });
            }
        });
        return keywords.sort((a, b) => b.count - a.count);
    };

    const computeThreeWordKeywords = (): Keyword[] => {
        const tokens = getTokens();
        const total = tokens.length;
        const freq: Record<string, number> = {};
        for (let i = 0; i < tokens.length - 2; i++) {
            const phrase = `${tokens[i]} ${tokens[i + 1]} ${tokens[i + 2]}`;
            freq[phrase] = (freq[phrase] || 0) + 1;
        }
        const keywords: Keyword[] = [];
        Object.entries(freq).forEach(([phrase, count]) => {
            if (count > 1) {
                keywords.push({
                    phrase,
                    count,
                    percentage: total > 0 ? (count / total) * 100 : 0
                });
            }
        });
        return keywords.sort((a, b) => b.count - a.count);
    };

    let keywords: Keyword[] = [];
    if (keywordCategory === 1) keywords = computeOneWordKeywords();
    else if (keywordCategory === 2) keywords = computeTwoWordKeywords();
    else if (keywordCategory === 3) keywords = computeThreeWordKeywords();

    useEffect(() => {
        setCurrentPage(1);
    }, [text, keywordCategory]);

    const totalPages = Math.ceil(keywords.length / PAGE_SIZE);
    const displayedKeywords = keywords.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <>
            <Helmet>
                <title>My IP</title>
                <meta
                    name="description"
                    content="Effortlessly analyze your text with our Word Counter—get accurate counts for words, characters, sentences, syllables, and more."
                />
            </Helmet>

            <Container fluid className={classes.container}>
                <Row>
                    <Col xl={2} className="d-none d-xl-flex justify-content-center">
                        <VerticalAd adSlot={LEFT_SLOT} />
                    </Col>

                    <Col xs={12} xl={8} className={elements.mainContent}>
                        <header className={elements.introduction}>
                            <h1 className={elements.title}>Word Counter</h1>
                            <p className={elements.description}>
                                Analyze your text – count words, characters, sentences, syllables, paragraphs and more.
                            </p>
                        </header>

                        <HorizontalAd adSlot={SLOT1} />

                        {/* Main Content */}
                        <Row className={classes.mainContent}>
                            <Col md={8} xs={12} className={classes.textCol}>
                                <div className={classes.textBlock}>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type or paste your text here..."
                className={classes.textInput}
            />
                                </div>
                            </Col>
                            <Col md={4} xs={12} className={classes.sidebar}>
                                {/* Stats Block */}
                                <div className={classes.statsBlock}>
                                    <div className={classes.statsRow}>
                                        <div className={classes.statItem}>
                                            <div className={classes.statLabel}>Words</div>
                                            <div className={classes.value}>{words}</div>
                                        </div>
                                        <div className={classes.statItem}>
                                            <div className={classes.statLabel}>Characters</div>
                                            <div className={classes.value}>{characters}</div>
                                        </div>
                                        <div className={classes.statItem}>
                                            <div className={classes.statLabel}>Sentences</div>
                                            <div className={classes.value}>{sentences}</div>
                                        </div>
                                    </div>
                                    <div className={classes.statsRow}>
                                        <div className={classes.statItem}>
                                            <div className={classes.statLabel}>Syllables</div>
                                            <div className={classes.value}>{syllables}</div>
                                        </div>
                                        <div className={classes.statItem}>
                                            <div className={classes.statLabel}>Paragraphs</div>
                                            <div className={classes.value}>{paragraphs}</div>
                                        </div>
                                    </div>
                                    <div className={classes.statsRow}>
                                        <div className={classes.statItem}>
                                            <div className={classes.statLabel}>Reading Time</div>
                                            <div className={classes.value}>{readingTime}</div>
                                        </div>
                                        <div className={classes.statItem}>
                                            <div className={classes.statLabel}>Speaking Time</div>
                                            <div className={classes.value}>{speakingTime}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Keywords Block */}
                                <div className={classes.keywordsBlock}>
                                    <div className={classes.keywordsHeader}>
                                        <h2 className={classes.keywordsTitle}>Top Keywords</h2>
                                        <div className={classes.keywordButtons}>
                                            <button
                                                className={`${classes.keywordButton} ${keywordCategory === 1 ? classes.active : ""}`}
                                                onClick={() => setKeywordCategory(1)}
                                            >
                                                1-word
                                            </button>
                                            <button
                                                className={`${classes.keywordButton} ${keywordCategory === 2 ? classes.active : ""}`}
                                                onClick={() => setKeywordCategory(2)}
                                            >
                                                2-word
                                            </button>
                                            <button
                                                className={`${classes.keywordButton} ${keywordCategory === 3 ? classes.active : ""}`}
                                                onClick={() => setKeywordCategory(3)}
                                            >
                                                3-word
                                            </button>
                                        </div>
                                    </div>
                                    { keywords.length === 0 ? (
                                        <div className={classes.noKeywords}>
                                            No keywords found
                                        </div>
                                    ) : (
                                        <>
                                            <div className={classes.keywordsTable}>
                                                {displayedKeywords.map((keyItem, index) => (
                                                    <div key={index} className={classes.keywordRow}>
                                                        <div className={classes.keywordPhrase}>{keyItem.phrase}</div>
                                                        <div className={classes.keywordStats}>
                                                            {keyItem.count} ({keyItem.percentage.toFixed(1)}%)
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            { totalPages > 1 && (
                                                <div className={classes.pagination}>
                                                    <button
                                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        &lt;
                                                    </button>
                                                    <span className={classes.pageInfo}>
                      {currentPage} / {totalPages}
                    </span>
                                                    <button
                                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        &gt;
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Col>

                            <HorizontalAd adSlot={SLOT2} />
                        </Row>
                    </Col>

                    <Col xl={2} className="d-none d-xl-flex justify-content-center">
                        <VerticalAd adSlot={RIGHT_SLOT} />
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default WordCounter;